// Persistent background-music singleton, independent of React mount/unmount, so
// the song chosen on the title screen keeps playing across Title -> Character ->
// Level select, and is only stopped when gameplay/bonus takes over the audio.
const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/game-assets`;

let audio = null;
let curFile = "";
let playlist = [];
const subs = new Set();
const endSubs = new Set();
const notify = () => subs.forEach((f) => { try { f(); } catch (e) { /* noop */ } });

// Ordered list of song files; when a track ends we advance to the next one.
export const setPlaylist = (files) => { playlist = (files || []).filter(Boolean); };

const ensure = () => {
  if (!audio) {
    audio = new Audio();
    audio.loop = true;
    audio.volume = 0.6;
    audio.addEventListener("play", notify);
    audio.addEventListener("pause", notify);
    audio.addEventListener("ended", () => {
      endSubs.forEach((f) => { try { f(); } catch (e) { /* noop */ } });
      // auto-advance through the playlist (only when not looping a single track)
      if (!audio.loop && playlist.length) {
        const i = playlist.indexOf(curFile);
        const next = playlist[(i + 1) % playlist.length];
        if (next) playBg(next);
      }
    });
  }
  return audio;
};

export const subscribeBg = (fn) => { subs.add(fn); return () => subs.delete(fn); };
// Fires when the current track finishes (only useful when looping is off).
export const onBgEnded = (fn) => { endSubs.add(fn); return () => endSubs.delete(fn); };
// Toggle looping — playlists set this false so tracks can auto-advance.
export const setBgLoop = (v) => { ensure().loop = !!v; };
export const isBgPlaying = () => !!audio && !audio.paused && !audio.ended;
export const bgFile = () => curFile;

// Load (if new) and play the given song file.
export const playBg = (file) => {
  const a = ensure();
  if (file && file !== curFile) { curFile = file; a.src = `${ASSET_BASE}/${file}`; }
  return a.play().then(notify).catch(() => {});
};

// Resume whatever song is already loaded (used when returning to a menu scene).
export const resumeBg = () => (audio && curFile ? audio.play().then(notify).catch(() => {}) : Promise.resolve());

export const toggleBg = () => {
  if (!audio) return;
  if (audio.paused) audio.play().then(notify).catch(() => {});
  else audio.pause();
};

// Hard stop — gameplay/bonus takes over the audio channel. Keeps the loaded
// src so resumeBg() can restart it when the player comes back to a menu scene.
export const stopTitleAudio = () => {
  if (audio) { audio.pause(); try { audio.currentTime = 0; } catch (e) { /* noop */ } }
};

// Kept for API compatibility (audio is now a JS singleton, not a React ref).
export const setTitleAudio = () => {};
